import React from 'react';
import QRCode from 'qrcode';
import InvoiceLetterhead from '../components/InvoiceLetterhead';
import { ReceiptLetterhead, QuotationLetterhead, CreditNoteLetterhead } from '../components/DocumentLetterheads';

// ── Finance documents: ONE source for web and the Android apps ──────────────
// Invoice, receipt, credit note and quotation documents are the letterhead
// components (components/InvoiceLetterhead.js, DocumentLetterheads.js) driven
// by Billing Settings. These builders turn a document + settings into the
// letterhead element, and every caller uses them:
//   • web Finance (pages/Finance.js) → printComponent() → browser "Save as PDF"
//   • Business / Client apps (mobile/pdf.js) → rendered off-screen → PDF file
// So a change to a letterhead, a Billing Settings option, or the mapping below
// shows up on the web and on the phones alike. Don't build these documents
// anywhere else.

export const paymentModeLabel = (mode, paymentModes = []) =>
  paymentModes.find(pm => pm.value === mode)?.name
  || (mode ? String(mode).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-');

async function upiQr(s, amount, note) {
  if (!s?.upi_id || s.show_invoice_qr === false || !(Number(amount) > 0)) return '';
  const upi = `upi://pay?${new URLSearchParams({ pa: s.upi_id, pn: s.firm_name || 'ChanakyaX', am: String(amount || ''), cu: 'INR', tn: note }).toString()}`;
  try { return await QRCode.toDataURL(upi, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); return ''; }
}

// Invoice detail rows (staff GET /api/invoices/:id and the client portal's)
// already carry the Billing Settings columns, so the invoice is its own settings.
export async function invoiceDocument(invoice) {
  const amountForQr = Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice.total_amount;
  const qrDataUrl = await upiQr(invoice, amountForQr, `Invoice ${invoice.invoice_number}`);
  return {
    title: `Invoice ${invoice.invoice_number}`,
    element: <InvoiceLetterhead settings={invoice} invoice={invoice} qrDataUrl={qrDataUrl} />,
  };
}

export async function receiptDocument(receipt, settings, paymentModes = []) {
  const qrDataUrl = await upiQr(settings, receipt.balance_after, `Receipt ${receipt.receipt_number || ''}`);
  return {
    title: `Receipt ${receipt.receipt_number || ''}`,
    element: <ReceiptLetterhead settings={settings} receipt={{ ...receipt, mode_label: paymentModeLabel(receipt.mode, paymentModes) }} qrDataUrl={qrDataUrl} />,
  };
}

export async function creditNoteDocument(creditNote, items, settings) {
  return {
    title: `Credit Note ${creditNote.credit_note_number || ''}`,
    element: <CreditNoteLetterhead settings={settings} creditNote={{ ...creditNote, items: items || creditNote.items || [] }} />,
  };
}

export async function quotationDocument(quote, settings) {
  const items = Array.isArray(quote.items) ? quote.items : (typeof quote.items === 'string' ? JSON.parse(quote.items || '[]') : []);
  return {
    title: `Quotation ${quote.quote_number || ''}`,
    element: <QuotationLetterhead settings={settings} quote={{ ...quote, items, total_amount: Number(quote.total_amount || quote.amount || 0) }} />,
  };
}
